import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Download, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { getImage } from "@/lib/api";
import { BACKEND_URL, imageUrl } from "@/lib/api";

export default function ImageDetail() {
  const { id } = useParams();
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setImage(null);
    setError(null);
    getImage(id)
      .then((d) => {
        if (active) setImage(d);
      })
      .catch((e) => {
        if (active) setError(e?.response?.data?.detail || "Image not found");
      });
    return () => {
      active = false;
    };
  }, [id]);

  const fullUrl = image ? `${BACKEND_URL}${imageUrl(image.id)}` : "";
  const shareUrl =
    typeof window !== "undefined" ? `${window.location.origin}/image/${id}` : "";

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const download = async () => {
    const res = await fetch(fullUrl);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `${id}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
    toast.success("Downloaded");
  };

  if (error) {
    return (
      <div className="p-12 max-w-2xl mx-auto" data-testid="detail-error">
        <Link to="/" className="label-eyebrow hover:underline">
          ← Back
        </Link>
        <h1 className="font-display text-4xl uppercase mt-6">{error}</h1>
      </div>
    );
  }

  if (!image) {
    return (
      <div className="p-12 font-mono text-sm blink" data-testid="detail-loading">
        LOADING WORK
      </div>
    );
  }

  return (
    <div data-testid="image-detail-page" className="border-t-0">
      <div className="px-6 md:px-12 py-6 border-b-2 border-black">
        <Link
          to="/"
          data-testid="back-link"
          className="inline-flex items-center gap-2 label-eyebrow hover:bg-black hover:text-white px-2 py-1 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to gallery
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Image side */}
        <div className="lg:col-span-7 xl:col-span-8 bg-[#F0F0F0] border-b-2 lg:border-b-0 lg:border-r-2 border-black">
          <div className="sticky top-[73px] p-6 md:p-12 flex items-center justify-center min-h-[60vh]">
            <img
              data-testid="detail-image"
              src={fullUrl}
              alt={image.prompt}
              className="max-w-full max-h-[80vh] border-2 border-black bg-white shadow-brutal"
            />
          </div>
        </div>

        {/* Meta side */}
        <div className="lg:col-span-5 xl:col-span-4 p-6 md:p-12 space-y-8">
          <div>
            <div className="label-eyebrow text-muted-foreground">Prompt</div>
            <p
              data-testid="detail-prompt"
              className="mt-3 font-display text-2xl md:text-3xl leading-tight tracking-tight"
            >
              “{image.prompt}”
            </p>
          </div>

          <div className="grid grid-cols-2 border-2 border-black">
            <div className="p-4 border-r-2 border-b-2 border-black">
              <div className="label-eyebrow text-muted-foreground">Model</div>
              <div
                data-testid="detail-model"
                className="font-mono mt-2 text-sm"
              >
                {image.model === "openai"
                  ? "OpenAI GPT-Image-1"
                  : "Gemini Nano-Banana"}
              </div>
            </div>
            <div className="p-4 border-b-2 border-black">
              <div className="label-eyebrow text-muted-foreground">
                Variant
              </div>
              <div className="font-mono mt-2 text-sm">
                {image.provider_model}
              </div>
            </div>
            <div className="p-4 border-r-2 border-black">
              <div className="label-eyebrow text-muted-foreground">
                Created
              </div>
              <div className="font-mono mt-2 text-sm">
                {new Date(image.created_at).toLocaleString()}
              </div>
            </div>
            <div className="p-4">
              <div className="label-eyebrow text-muted-foreground">Size</div>
              <div className="font-mono mt-2 text-sm">
                {(image.size / 1024).toFixed(1)} KB
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="label-eyebrow text-muted-foreground">
              Permanent URL
            </div>
            <div
              data-testid="detail-url"
              className="font-mono text-xs md:text-sm break-all border-2 border-black p-3 bg-white"
            >
              {fullUrl}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => copy(fullUrl, "URL")}
                data-testid="detail-copy-url"
                className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-black bg-white hover:bg-black hover:text-white label-eyebrow transition-colors"
              >
                <Copy className="w-4 h-4" /> Copy image URL
              </button>
              <button
                type="button"
                onClick={() => copy(shareUrl, "Share link")}
                data-testid="detail-copy-share"
                className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-black bg-white hover:bg-black hover:text-white label-eyebrow transition-colors"
              >
                <LinkIcon className="w-4 h-4" /> Copy page link
              </button>
            </div>
            <button
              type="button"
              onClick={download}
              data-testid="detail-download"
              className="w-full flex items-center justify-center gap-2 px-4 py-4 border-2 border-black bg-[#FF3333] text-white hover:bg-black label-eyebrow transition-colors"
            >
              <Download className="w-4 h-4" /> Download PNG
            </button>
          </div>

          <div className="pt-6 border-t-2 border-black">
            <div className="label-eyebrow text-muted-foreground">ID</div>
            <div
              data-testid="detail-id"
              className="font-mono text-xs mt-2 break-all"
            >
              {image.id}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
